"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/* ─────────────────────────────────────────────────────────────
   Theme system

   Three values:
     "light"   — force light regardless of OS (DEFAULT for new users)
     "dark"    — force dark regardless of OS
     "system"  — follow prefers-color-scheme

   Default rationale: Gate36's brand (warm-paper canvas, coral, painted
   illustrations) is designed in light mode. First-time visitors land
   on the intentional brand version regardless of their OS preference.
   A Mac user with system dark mode will still see Gate36's light
   brand on first visit, then can flip to dark/system in profile.

   Persistence:
     localStorage["gate36_theme"]  — the source of truth on the client.
     The no-flash boot script in layout.tsx reads this BEFORE hydration
     and applies .dark synchronously so there is never a white flash.
     For signed-in users, User.theme on the DB syncs across devices.

   Native: when theme changes, sync the Capacitor StatusBar style so
   the notch bar matches the canvas. Done via dynamic import so the
   web build doesn't bundle the native plugin.
   ───────────────────────────────────────────────────────────── */

export type Theme = "light" | "dark" | "system";
const STORAGE_KEY = "gate36_theme";
const DEFAULT_THEME: Theme = "light";

type ThemeCtx = {
  theme: Theme;
  resolved: "light" | "dark";
  setTheme: (t: Theme) => void;
};

const Ctx = createContext<ThemeCtx | null>(null);

function getSystem(): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyDOM(resolved: "light" | "dark") {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
}

async function syncNativeStatusBar(resolved: "light" | "dark") {
  if (typeof window === "undefined") return;
  // Only attempt the import on a Capacitor-native platform — web no-op.
  // We check window.Capacitor (set by the bridge) before importing so the
  // web build doesn't try to resolve native code paths.
  // @ts-expect-error — Capacitor injects this at runtime
  if (!window.Capacitor?.isNativePlatform?.()) return;
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({
      style: resolved === "dark" ? Style.Dark : Style.Light,
    });
    await StatusBar.setBackgroundColor({
      color: resolved === "dark" ? "#0B0B0D" : "#FDFBF7",
    });
  } catch {
    // Plugin not registered (e.g. dev web) — silently skip.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Initial theme is read from localStorage / dataset on mount. We
  // can't read localStorage during SSR so default to DEFAULT_THEME
  // ("light") and let useEffect reconcile.
  const [theme, setThemeState] = useState<Theme>(DEFAULT_THEME);
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  // Hydrate from storage + DOM dataset (set by the no-flash boot script)
  useEffect(() => {
    const stored =
      (document.documentElement.dataset.theme as Theme | undefined) ??
      (localStorage.getItem(STORAGE_KEY) as Theme | null) ??
      DEFAULT_THEME;
    setThemeState(stored);
    const r = stored === "system" ? getSystem() : stored;
    setResolved(r);
    applyDOM(r);
    void syncNativeStatusBar(r);
  }, []);

  // When system mode is active, listen to the OS preference and react.
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => {
      const r = e.matches ? "dark" : "light";
      setResolved(r);
      applyDOM(r);
      void syncNativeStatusBar(r);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_KEY, t);
      document.documentElement.dataset.theme = t;
    } catch {
      /* private mode */
    }
    const r = t === "system" ? getSystem() : t;
    setResolved(r);
    applyDOM(r);
    void syncNativeStatusBar(r);
  }, []);

  const value = useMemo<ThemeCtx>(
    () => ({ theme, resolved, setTheme }),
    [theme, resolved, setTheme],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme must be used inside <ThemeProvider>");
  return v;
}
